// src/components/auth/GoogleConsentStep.tsx
//
// Shown immediately after a successful Google sign-in for NEW users
// (no Firestore profile exists yet). The user has authenticated
// with Google but hasn't given GDPR consent. This is a focused step:
// just age verification + terms + privacy + optional marketing.
// Returning users skip this entirely — they signed in directly.
//
// Decline → cancelGoogleConsent() deletes the Firebase auth user
// and returns to the previous state. Accept → completeGoogleConsent
// writes the profile and signs the user in.

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  Avatar,
  Stack,
  RadioGroup,
  Radio,
  Link,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useT } from '../../i18n/useT';

interface Props {
  onComplete: () => void;
}

export const GoogleConsentStep: React.FC<Props> = ({ onComplete }) => {
  const t = useT();
  const {
    pendingGoogleConsent,
    completeGoogleConsent,
    cancelGoogleConsent,
    error,
    isLoading,
  } = useAuthStore();

  const [ageVerification, setAgeVerification] = useState<'over16' | 'under16' | ''>('');
  const [showAgeError, setShowAgeError] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canAccept =
    ageVerification === 'over16' && acceptedTerms && acceptedPrivacy;

  if (!pendingGoogleConsent) {
    // Shouldn't happen, but render nothing rather than crash if the
    // pending state was cleared while this component was mounted.
    return null;
  }

  const handleAccept = async () => {
    if (ageVerification === 'under16') {
      setShowAgeError(true);
      return;
    }
    if (!canAccept) return;

    try {
      setSubmitting(true);
      await completeGoogleConsent({
        necessary: true,
        functional: true,
        analytics: marketingConsent,
        marketing: marketingConsent,
        consentDate: new Date(),
        privacyPolicyVersion: '3.0',
        ageVerified: true,
        ageVerificationDate: new Date(),
      });
      onComplete();
    } catch {
      // Error is surfaced via the store's error field
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    try {
      setSubmitting(true);
      await cancelGoogleConsent();
      onComplete();
    } finally {
      setSubmitting(false);
    }
  };

  const initials = (pendingGoogleConsent.displayName || pendingGoogleConsent.email || '?')
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <Box sx={{ pt: 1 }}>
      {/* Account summary — confirms which Google account they're
          continuing with. Especially helpful if they accidentally
          picked the wrong one. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          mb: 2,
          borderRadius: 1.5,
          bgcolor: 'action.hover',
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main' }}>{initials}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
            {pendingGoogleConsent.displayName || t('Continuing with Google')}
          </Typography>
          {pendingGoogleConsent.email && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {pendingGoogleConsent.email}
            </Typography>
          )}
        </Box>
        <CheckCircleOutline sx={{ ml: 'auto', color: 'success.main' }} />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        
        {t("One last step: confirm your age and accept our terms to finish creating your account.")}
      </Typography>

      {/* Age verification — required */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 0.5 }}
          component="label"
        >
          
          {t("Age verification *")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          
          {t("You must be 16 or older to use Instant Tours.")}
        </Typography>
        <RadioGroup
          value={ageVerification}
          onChange={(e) => {
            setAgeVerification(e.target.value as 'over16' | 'under16');
            setShowAgeError(false);
          }}
        >
          <FormControlLabel
            value="over16"
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">{t("I am 16 years of age or older")}</Typography>
            }
          />
          <FormControlLabel
            value="under16"
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">{t("I am under 16 years of age")}</Typography>
            }
          />
        </RadioGroup>
        {showAgeError && ageVerification === 'under16' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            
            {t("We're sorry, but you must be 16 or older to use Instant Tours. Please ask a parent or guardian for help.")}
          </Alert>
        )}
      </Box>

      {/* Terms + Privacy (required) */}
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              
              {t("I accept the")}{' '}
              <Link href="/terms" target="_blank" rel="noopener" underline="hover">
                
                {t("Terms of Service")}
              </Link>
              {' *'}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedPrivacy}
              onChange={(e) => setAcceptedPrivacy(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              
              {t("I accept the")}{' '}
              <Link href="/privacy" target="_blank" rel="noopener" underline="hover">
                
                {t("Privacy Policy")}
              </Link>
              {' *'}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              
              {t("Send me product updates and travel tips (optional)")}
            </Typography>
          }
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
        <Button
          variant="text"
          onClick={handleDecline}
          disabled={submitting || isLoading}
          sx={{ textTransform: 'none' }}
        >
          
          {t("Cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!canAccept || submitting || isLoading}
          sx={{ textTransform: 'none' }}
        >
          {submitting || isLoading ? 'Finishing…' : 'Accept and finish'}
        </Button>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2, textAlign: 'center' }}
      >
        
        {t("Cancelling will discard this account.")}
      </Typography>
    </Box>
  );
};
